import { publicRequest } from "../../../requestMeth";

/**
 * Servicio para consumir los endpoints de reportes con IA
 * Actúa como intermediario entre los componentes y el backend
 */

/**
 * Genera un informe técnico basado en un prompt usando IA
 * @param {string} prompt - Solicitud de informe libre del usuario
 * @param {string} sessionId - ID de sesión para mantener contexto de conversación
 * @returns {Promise<Object>} Respuesta con { success, message, result, artifacts }
 */
export const generateAiReport = async (
  prompt,
  sessionId,
  userId,
  context,
  token,
  analysisContext,
  history,
) => {
  try {
    const payload = {
      prompt,
    };
    
    // Incluir userId si está disponible
    if (userId) {
      payload.userId = userId;
    }
    
    // Incluir sessionId si está disponible
    if (sessionId) {
      payload.sessionId = sessionId;
    }

    if (context) {
      payload.context = context;
    }

    if (analysisContext) {
      payload.analysisContext = analysisContext;
    }

    if (Array.isArray(history)) {
      payload.history = history;
    }
    
    const response = await publicRequest.post("/ai/reports", payload, {
      headers: token ? { token: `Bearer ${token}` } : {},
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || "Error al generar informe",
      status: error?.response?.status || 500,
    };
  }
};

/**
 * Obtiene el historial de reportes generados (preparado para futuras expansiones)
 * @param {Object} filters - Filtros opcionales { limit, offset, dateFrom, dateTo }
 * @returns {Promise<Object>} Lista de reportes
 */
export const getAiReportsHistory = async (filters = {}, token) => {
  try {
    const response = await publicRequest.get("/ai/reports/history", {
      params: filters,
      headers: token ? { token: `Bearer ${token}` } : {},
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || "Error al obtener historial",
      status: error?.response?.status || 500,
    };
  }
};

export const getAiReportSessionHistory = async (sessionId, token) => {
  try {
    const response = await publicRequest.get("/ai/session-history", {
      params: {
        sessionId,
      },
      headers: token ? { token: `Bearer ${token}` } : {},
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error?.response?.data?.message || error?.message ||
        "Error al obtener el historial de la sesión",
      status: error?.response?.status || 500,
    };
  }
};

/**
 * Obtiene los detalles de un reporte específico
 * @param {number|string} reportId - ID del reporte
 * @returns {Promise<Object>} Detalles del reporte
 */
export const getAiReportDetail = async (reportId, token) => {
  try {
    const response = await publicRequest.get(`/ai/reports/${reportId}`, {
      headers: token ? { token: `Bearer ${token}` } : {},
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || "Error al obtener reporte",
      status: error?.response?.status || 500,
    };
  }
};
